import { useViewerMaybe } from "@/authentication/ViewerProvider"
import { Alert } from "@/components/ui/alert"
import { FinchAccountStatus } from "@/generated/graphql"
import LoadingScreen from "@/novus/components/loading-screen"
import { MainLayout } from "@/org-admins/layouts/MainLayout"
import { signInPath } from "@/org-admins/org-admins-paths"
import { CircleCheck } from "lucide-react"
import * as React from "react"
import { useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { FinchConnectWidget } from "./components/FinchConnectWidget"

export const DashboardScreen = () => {
  const { viewer, loading } = useViewerMaybe()
  const navigate = useNavigate()
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!(loading || viewer)) {
      navigate(signInPath.pattern)
    }
  }, [navigate, viewer, loading])

  if (!viewer || loading) {
    return <LoadingScreen />
  }

  const finchConnectionSuccessful = useMemo(
    () =>
      viewer.organization?.finchAccountStatus &&
      [
        FinchAccountStatus.Connected,
        FinchAccountStatus.Pending,
        FinchAccountStatus.Processing,
      ].includes(viewer.organization.finchAccountStatus),
    [viewer],
  )

  const finchConnectionError = useMemo(
    () =>
      viewer.organization?.finchAccountStatus &&
      [
        FinchAccountStatus.ErrorPermissions,
        FinchAccountStatus.ErrorNoAccountSetup,
      ].includes(viewer.organization.finchAccountStatus),
    [viewer],
  )

  return (
    <MainLayout>
      <div className="container justify-content-between w-10/12 md:w-8/12 mx-auto mt-12 mb-12 md:my-[100px] pb-14 max-w-[650px] ">
        <h1 className="text-4xl font-bold mb-8">
          Welcome {viewer.firstName} {viewer.lastName}!
        </h1>
        <div className="bg-white rounded-[40px] p-8">
          <div className="flex flex-direction-row gap-4 justify-between items-center">
            <div>
              <h2 className="text-neutral-400">Organization</h2>
              <div className="font-bold text-xl">
                {viewer.organization?.name}
              </div>
            </div>

            {finchConnectionSuccessful ? (
              <CircleCheck color="#1ec167" size={50} />
            ) : null}
          </div>
          {finchConnectionSuccessful && (
            <div className="border mt-8 p-8 text-center text-lg">
              Your account has <span className="font-bold">successfully</span>{" "}
              been connected.
            </div>
          )}
          {(error || finchConnectionError) && (
            <Alert variant="error" className="mt-8">
              Account link was unsuccessful. Please try again.
            </Alert>
          )}
          {!finchConnectionSuccessful && (
            <div className="mt-8">
              <hr />
              <div className="mt-8 text-center">
                <h2 className="text-4xl font-bold">
                  Easily connect your payroll to unlock 529 benefits
                </h2>
                <p className="mt-4 mb-8 text-xl">
                  Sync your payroll system securely to enable automatic
                  contributions to employee 529 savings accounts. No manual
                  uploads—just seamless, tax-advantaged savings.
                </p>
                <FinchConnectWidget setError={setError} />
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  )
}
